import { useState } from 'react';
import {
  isFlythroughResultAlreadyInHistory,
  SAVED_FLYTHROUGH_HISTORY_LIMIT,
} from '../utils/photorealistic/flythroughHistoryDisplay';

function DownloadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

function PlayBadge() {
  return (
    <span className="absolute inset-0 flex items-center justify-center bg-black/15">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <polygon points="8 5 19 12 8 19 8 5" />
        </svg>
      </span>
    </span>
  );
}

function getSaveItemTooltip({ item, serverFlythroughHistory, isSaving }) {
  if (isSaving) return 'Saving to video library…';
  if (isFlythroughResultAlreadyInHistory(item, serverFlythroughHistory)) {
    return 'Already saved to video library';
  }
  return 'Save to video library';
}

const FlythroughHistoryGrid = ({
  items = [],
  columns = SAVED_FLYTHROUGH_HISTORY_LIMIT,
  renderingJobs = [],
  onSelectItem,
  onSaveItem,
  onDownloadItem,
  onDeleteItem,
  savingItemId = null,
  downloadingItemId = null,
  serverFlythroughHistory = [],
  deletingItemId = null,
  className = '',
}) => {
  const [failedThumbnailIds, setFailedThumbnailIds] = useState({});
  const orderedRenderingJobs = [...renderingJobs].sort(
    (a, b) => Number(a?.submittedAt ?? 0) - Number(b?.submittedAt ?? 0),
  );

  if (!items.length && !orderedRenderingJobs.length) return null;

  return (
    <ul
      className={`grid gap-1.5 ${className}`.trim()}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {orderedRenderingJobs.map((job) => (
        <li key={job.jobId} className="min-w-0">
          <div
            className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm"
            aria-label={`Flythrough in progress: ${job.statusLabel}`}
            role="status"
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[1px]">
              <svg
                className="h-6 w-6 animate-spin text-primary-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="mt-1.5 text-[10px] font-medium capitalize text-primary-300">
                {job.statusLabel || 'on queue'}
              </span>
            </div>
          </div>
        </li>
      ))}

      {items.slice(0, SAVED_FLYTHROUGH_HISTORY_LIMIT).map((item) => {
        const key = String(item?.id ?? item?.createdAt ?? item?.videoUrl);
        const itemIdKey = String(item?.id ?? key);
        const thumbnailUrl = item?.imageUrl ?? item?.lightboxImageUrl ?? null;
        const thumbnailFailed = Boolean(failedThumbnailIds[itemIdKey]);
        const showThumbnail = Boolean(thumbnailUrl) && !thumbnailFailed;
        const posterVideoUrl = !showThumbnail && item?.videoUrl ? item.videoUrl : null;
        const isDeleting = deletingItemId != null && String(deletingItemId) === itemIdKey;
        const isSaving = savingItemId != null && String(savingItemId) === itemIdKey;
        const isDownloading = downloadingItemId != null && String(downloadingItemId) === itemIdKey;
        const hasSave = Boolean(onSaveItem);
        const hasDelete = Boolean(onDeleteItem);
        const hasDownload = Boolean(onDownloadItem);
        const downloadDisabled =
          isDownloading ||
          isDeleting ||
          !item?.videoUrl ||
          (downloadingItemId != null && !isDownloading);
        const showActions = hasSave || hasDelete || hasDownload;
        const alreadySaved = isFlythroughResultAlreadyInHistory(item, serverFlythroughHistory);
        const saveDisabled =
          isSaving ||
          isDeleting ||
          alreadySaved ||
          (savingItemId != null && !isSaving) ||
          (deletingItemId != null && !isDeleting);
        const saveTooltip = getSaveItemTooltip({
          item,
          serverFlythroughHistory,
          isSaving,
        });

        return (
          <li key={key} className="min-w-0">
            <div className="relative aspect-[4/3] w-full">
              <button
                type="button"
                onClick={() => onSelectItem?.(item)}
                disabled={isDeleting || !item?.videoUrl}
                className="group block h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
                title="Play flythrough video"
              >
                {showThumbnail ? (
                  <img
                    src={thumbnailUrl}
                    alt="Flythrough video"
                    className="h-full w-full object-cover"
                    loading="lazy"
                    draggable={false}
                    referrerPolicy="no-referrer"
                    onError={() => {
                      setFailedThumbnailIds((prev) => ({
                        ...prev,
                        [itemIdKey]: true,
                      }));
                    }}
                  />
                ) : posterVideoUrl ? (
                  <video
                    src={posterVideoUrl}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-gray-100 text-[10px] text-gray-400">
                    Video
                  </span>
                )}
                {item?.videoUrl ? <PlayBadge /> : null}
              </button>

              {showActions ? (
                <div className="absolute right-1 top-1 z-10 flex items-center gap-1">
                  {hasDownload ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!downloadDisabled) onDownloadItem(item);
                      }}
                      disabled={downloadDisabled}
                      className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-white hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Download flythrough video"
                      aria-label="Download flythrough video"
                    >
                      {isDownloading ? (
                        <svg
                          className="h-3 w-3 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : (
                        <DownloadIcon />
                      )}
                    </button>
                  ) : null}

                  {hasSave ? (
                    <span title={saveTooltip}>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!saveDisabled) onSaveItem(item);
                        }}
                        disabled={saveDisabled}
                        className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-white hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={saveTooltip}
                      >
                        {isSaving ? (
                          <svg
                            className="h-3 w-3 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                            <polyline points="17 21 17 13 7 13 7 21" />
                            <polyline points="7 3 7 8 15 8" />
                          </svg>
                        )}
                      </button>
                    </span>
                  ) : null}

                  {hasDelete ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteItem(item);
                      }}
                      disabled={isDeleting || deletingItemId != null}
                      className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:bg-white hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Delete flythrough"
                      aria-label="Delete flythrough"
                    >
                      {isDeleting ? (
                        <svg
                          className="h-3 w-3 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 6L6 18" />
                          <path d="M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const SavedFlythroughHistoryStrip = ({
  visible = false,
  items = [],
  loading = false,
  renderingJobs = [],
  error = null,
  onSelectItem,
  onSaveItem,
  onDeleteItem,
  savingItemId = null,
  serverFlythroughHistory = [],
  deletingItemId = null,
}) => {
  if (!visible) return null;

  const showGrid = items.length > 0 || renderingJobs.length > 0;

  return (
    <section
      className="mx-auto mb-3 w-full max-w-[min(100%,1024px)] shrink-0 border-t border-gray-200/80 bg-white/50 px-3 py-3 backdrop-blur-sm"
      aria-label="Recent flythrough videos"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Recent flythroughs
        </h4>
      </div>

      {loading && renderingJobs.length === 0 ? (
        <p className="text-center text-xs text-gray-500 py-1">Loading flythrough videos…</p>
      ) : error ? (
        <p className="text-center text-xs text-red-600 py-1">{error}</p>
      ) : !showGrid ? (
        <p className="text-center text-xs text-gray-500 py-1">
          No flythrough videos yet. Click Generate to create one.
        </p>
      ) : (
        <FlythroughHistoryGrid
          items={items}
          renderingJobs={renderingJobs}
          onSelectItem={onSelectItem}
          onSaveItem={onSaveItem}
          onDeleteItem={onDeleteItem}
          savingItemId={savingItemId}
          serverFlythroughHistory={serverFlythroughHistory}
          deletingItemId={deletingItemId}
          className="mx-auto max-w-full"
        />
      )}
    </section>
  );
};

export { FlythroughHistoryGrid };
export default SavedFlythroughHistoryStrip;
